import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { NAVY, RADIUS_COIN } from 'styles/tokens';

interface SubPillTabsProps<V extends string> {
  tabs: ReadonlyArray<{ value: V; label: string; count: number }>;
  /** `null` selects the leading "all" pill, which carries no count. */
  value: V | null;
  onChange: (value: V | null) => void;
  allLabel?: string;
}

/**
 * Secondary tab row nested under `PillTabs`. Deliberately quieter than it — this refines the tab
 * above rather than sitting beside it.
 */
const SubPillTabs = <V extends string>({
  tabs,
  value,
  onChange,
  allLabel = 'Todas',
}: SubPillTabsProps<V>): ReactNode => (
  <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, pl: 0.5 }}>
    {[{ value: null, label: allLabel, count: null }, ...tabs].map((tab) => {
      const isActive = value === tab.value;
      return (
        <Box
          key={tab.value ?? '__all__'}
          role="tab"
          aria-selected={isActive}
          onClick={() => onChange(tab.value)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.25,
            py: 0.35,
            borderRadius: RADIUS_COIN,
            cursor: 'pointer',
            bgcolor: isActive ? `${NAVY}12` : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': { bgcolor: isActive ? `${NAVY}12` : `${NAVY}06` },
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: isActive ? 700 : 500,
              fontSize: '0.7rem',
              color: isActive ? NAVY : `${NAVY}99`,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              textTransform: 'capitalize',
            }}
          >
            {tab.label}
          </Typography>
          {tab.count != null && (
            <Box
              component="span"
              sx={{
                fontFamily: 'Jost, sans-serif',
                fontSize: '0.6rem',
                fontWeight: 700,
                color: `${NAVY}60`,
                bgcolor: `${NAVY}0F`,
                borderRadius: '10px',
                minWidth: 16,
                height: 14,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 0.5,
                lineHeight: 1,
              }}
            >
              {tab.count}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

export default SubPillTabs;
